import { CHAT_LIMITS } from '@mogzo/shared';
import { ArrowLeft, X } from 'lucide-react';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { ApiError } from '../../lib/api';
import { findPeople, makeGroup, messagePlayer, refreshChatCounts } from '../../lib/chat';
import { Avatar } from '../community/Avatar';
import { Alert, buttonClass } from '../ui';

const fieldClass =
  'h-11 w-full rounded-full border border-transparent bg-field px-4 text-[15px] focus:border-ink focus:bg-card focus:outline-none';

/** Usernames starting with what's typed, as the player types. */
function usePeople(query: string) {
  const [people, setPeople] = useState<string[]>([]);
  useEffect(() => {
    const q = query.trim();
    if (!q) return setPeople([]);
    let live = true;
    const timer = window.setTimeout(() => {
      findPeople(q)
        .then((result) => live && setPeople(result.people.map((p) => p.username)))
        .catch(() => undefined);
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [query]);
  return people;
}

function Suggestions({ names, onPick }: { names: string[]; onPick: (name: string) => void }) {
  if (!names.length) return null;
  return (
    <ul className="m-0 mt-1.5 flex list-none flex-col p-0" aria-label="Players">
      {names.map((name) => (
        <li key={name}>
          <button
            type="button"
            onClick={() => onPick(name)}
            className="flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left font-semibold hover:bg-field"
          >
            <Avatar name={name} size="sm" />
            {name}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Starting a chat (M38): a message to someone by username, which waits in their Requests if
 * you're new to them, or a group of people you already chat with.
 */
export function NewChat({
  onBack,
  onStarted,
}: {
  onBack: () => void;
  onStarted: (conversationId: string) => void;
}) {
  const [mode, setMode] = useState<'direct' | 'group'>('direct');
  const [to, setTo] = useState('');
  const [text, setText] = useState('');
  const [groupName, setGroupName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const suggestions = usePeople(to).filter((name) => name !== to && !picked.includes(name));
  const ids = { to: useId(), text: useId(), name: useId() };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { conversationId } =
        mode === 'direct'
          ? await messagePlayer(to.trim(), text.trim())
          : await makeGroup(groupName.trim(), picked);
      refreshChatCounts();
      onStarted(conversationId);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const ready =
    mode === 'direct' ? to.trim() && text.trim() : groupName.trim() && picked.length > 0;

  return (
    <section aria-labelledby="new-chat" className="flex min-h-0 flex-1 flex-col bg-card">
      <header className="flex min-h-16 shrink-0 items-center gap-2 border-b border-line px-2">
        <button
          type="button"
          aria-label="Back to Messages"
          onClick={onBack}
          className="flex size-10 items-center justify-center rounded-full hover:bg-field"
        >
          <ArrowLeft size={20} strokeWidth={1.9} aria-hidden="true" />
        </button>
        <h2 id="new-chat" className="text-[17px] font-bold">
          New message
        </h2>
      </header>
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        <fieldset className="grid grid-cols-2 rounded-full border-0 bg-field p-1">
          <legend className="sr-only">Kind of chat</legend>
          {(['direct', 'group'] as const).map((value) => (
            <label
              key={value}
              className={`flex h-9 cursor-pointer items-center justify-center rounded-full text-sm font-semibold has-focus-visible:outline-2 ${mode === value ? 'bg-card text-ink shadow-[0_1px_2px_rgba(27,21,47,0.1)]' : 'text-muted'}`}
            >
              <input
                type="radio"
                name="mode"
                value={value}
                checked={mode === value}
                onChange={() => {
                  setMode(value);
                  setError(null);
                }}
                className="sr-only"
              />
              {value === 'direct' ? 'To one player' : 'A group'}
            </label>
          ))}
        </fieldset>

        {mode === 'group' && (
          <div>
            <label htmlFor={ids.name} className="mb-1.5 block font-semibold">
              Group name
            </label>
            <input
              id={ids.name}
              value={groupName}
              maxLength={CHAT_LIMITS.groupName}
              onChange={(event) => setGroupName(event.target.value)}
              className={fieldClass}
            />
          </div>
        )}

        <div>
          <label htmlFor={ids.to} className="mb-1.5 block font-semibold">
            {mode === 'direct' ? 'To' : 'People'}
          </label>
          {mode === 'group' && picked.length > 0 && (
            <ul className="m-0 mb-2 flex list-none flex-wrap gap-1.5 p-0">
              {picked.map((name) => (
                <li
                  key={name}
                  className="flex h-8 items-center gap-1 rounded-full bg-cream pr-1 pl-3 text-sm font-semibold"
                >
                  {name}
                  <button
                    type="button"
                    aria-label={`Take ${name} out`}
                    onClick={() => setPicked(picked.filter((n) => n !== name))}
                    className="flex size-6 items-center justify-center rounded-full hover:bg-card"
                  >
                    <X size={14} strokeWidth={2} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input
            id={ids.to}
            value={to}
            autoComplete="off"
            placeholder="Username"
            onChange={(event) => setTo(event.target.value)}
            className={fieldClass}
          />
          <Suggestions
            names={suggestions}
            onPick={(name) => {
              if (mode === 'direct') setTo(name);
              else if (picked.length < CHAT_LIMITS.groupSize - 1) {
                setPicked([...picked, name]);
                setTo('');
              }
            }}
          />
          {mode === 'group' && (
            <p className="mt-1.5 text-[13px] leading-[18px] text-muted">
              Add people you already chat with, up to {CHAT_LIMITS.groupSize - 1}.
            </p>
          )}
        </div>

        {mode === 'direct' && (
          <div>
            <label htmlFor={ids.text} className="mb-1.5 block font-semibold">
              Message
            </label>
            <textarea
              id={ids.text}
              value={text}
              rows={3}
              maxLength={CHAT_LIMITS.messageLength}
              onChange={(event) => setText(event.target.value)}
              className="w-full resize-none rounded-[18px] border border-transparent bg-field px-4 py-2.5 text-[15px] focus:border-ink focus:bg-card focus:outline-none"
            />
            <p className="mt-1 text-[13px] leading-[18px] text-muted">
              If you haven’t chatted before, it waits in their Requests until they accept.
            </p>
          </div>
        )}

        {error && <Alert>{error}</Alert>}
        <button type="submit" disabled={!ready || busy} className={buttonClass('primary')}>
          {busy ? 'Please wait…' : mode === 'direct' ? 'Send' : 'Make group'}
        </button>
      </form>
    </section>
  );
}
